import get from 'lodash/get';
import { messageBrokerConfig } from '@Constants/CommonConstants';
import SimpleTmpl from '@Services/SimpleTmpl';
import HttpRequest from '@Services/RequestFactory';
import history from '@AppRoutes/history';

const {
  RETRY_LIMIT,
  STATUS: { FINISHED, LIMIT_REACHED, PENDING, ABORT },
  repeatableStatusCodes,
} = messageBrokerConfig;

export default class MessageBrokerRequest {
  count = 0;

  canceled = false;

  constructor(
    { url, method, validErrorStatusCodes, nonRepeatable = false, fullResponseWithStatusCode = false },
    payload,
    options,
    defer,
    body
  ) {
    // eslint-disable-next-line no-param-reassign
    url = SimpleTmpl.formatOptional(url, payload);
    if ((method === 'GET' || method === 'DELETE') && payload) {
      // eslint-disable-next-line no-param-reassign
      payload = null;
    }

    this.url = url;
    this.method = method;
    this.validErrorStatusCodes = validErrorStatusCodes;
    this.nonRepeatable = nonRepeatable;
    this.fullResponseWithStatusCode = fullResponseWithStatusCode;
    this.payload = payload;
    this.options = options;
    this.defer = defer;
    this.body = body;
  }

  cancel() {
    if (this.canceled) return;

    this.canceled = true;
    if (this.httpPromise) {
      this.httpPromise.cancel();
    }
    this.defer.reject({ status: 'canceled' });
  }

  send() {
    const handleRetryLimit = () => {
      this.defer.reject({ status: 'Retry amount limit exceeded' });
      return Promise.reject(LIMIT_REACHED);
    };

    if (this.canceled) return Promise.reject();

    if (this.httpPromise) {
      if (this.nonRepeatable) {
        return this.httpPromise;
      }
      this.httpPromise.cancel();
    }

    if (this.count >= RETRY_LIMIT) {
      return handleRetryLimit();
    }

    this.count += 1;
    this.start = Date.now();
    this.httpPromise = HttpRequest.send(
      this.url,
      this.method,
      this.body ? this.body : this.payload,
      this.validErrorStatusCodes
        ? {
            ...this.options,
            validErrorStatusCodes: this.validErrorStatusCodes,
          }
        : this.options
    );

    return this.httpPromise
      .then((response) => {
        if (this.fullResponseWithStatusCode) {
          this.defer.resolve(response);
        } else {
          this.defer.resolve(response.data);
        }
        return Promise.resolve(FINISHED);
      })
      .catch((response) => {
        const status = get(response, 'status');
        const type = get(response, ['data', 'type']);

        const redirectToErrorPage = () => {
          if (this.url.indexOf('/oauth/token') >= 0) {
            if (type === 'EXTERNAL' || type === 'ERROR_RETRY') {
              history.push({
                pathname: '/error',
                state: { retry: true },
              });
            }
          }

          if (status === 404 || status === 504) {
            history.push({
              pathname: '/error',
              state: { retry: true },
            });
          }
        };

        if (this.canceled) return Promise.reject(ABORT);

        // In case when 404 error code is valid and means "resource not found"
        if (this.validErrorStatusCodes && this.validErrorStatusCodes.indexOf(status) !== -1) {
          this.defer.reject(response.data || response);
          return Promise.reject(FINISHED);
        }

        if (repeatableStatusCodes.indexOf(status) === -1) {
          this.defer.reject(response.data || response);
          redirectToErrorPage();
          return Promise.reject(FINISHED);
        }

        if (this.nonRepeatable || this.count >= RETRY_LIMIT) {
          redirectToErrorPage();
          return handleRetryLimit();
        }

        return Promise.reject(PENDING);
      });
  }

  time() {
    return Date.now() - this.start;
  }

  destroy() {
    this.canceled = true;
  }
}
